import { CheckBadgeIcon, FunnelIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import MaterialInvoiceTable from "../../../../../components/tables/MaterialInvoiceTable";
import CompletedFilters from "./TableFilter";
import { CompletedDetails } from "./CompletedDetails";
import useGetActivityHistoryById from "../../InvoiceCapture/hooks/useGetActivityHistoryId";
import { IItemize } from "../../InvoiceCapture/types";
import { useGetCompletedInvoices } from "../hooks/useGetCompletedInvoices";
import { useGetCompletedInvoiceById } from "../hooks/useGetCompletedInvoiceById";
import { completedData } from "../atom/atoms";
import { voidedData } from "../../InvoiceCapture/atoms/atoms";
import { useGetVoidedInvoiceById } from "../../InvoiceCapture/hooks/useGetVoidedInvoiceById";
import { useQueryClient } from "react-query";

interface CompletedProps {}

export const CompletedTable: React.FC<CompletedProps> = () => {
  const [invoiceTableData, setInvoiceTableData] = useState<any[]>([]);
  const [isShow, setIsShow] = useState(false);
  const [viewData, setViewData] = useState<any>([]);
  const [filter, setFilter] = useState(false);
  const [, setCompletedInvoiceDetails] = useRecoilState<any>(completedData);
  const [, setVoidedInvoiceDetails] = useRecoilState<any>(voidedData);
  const [isSearching, setIsSearching] = useState(false);
  const [filteredCompleted, setFilteredCompleted] = useState<any>([]);
  const queryClient = useQueryClient();

  const {
    data: invoiceData,
    isFetching: vendorInvoiceFetching,
    isSuccess: getVendorInvoiceSuccess,
  } = useGetCompletedInvoices();

  useEffect(() => {
    if (getVendorInvoiceSuccess && invoiceData) {
      setInvoiceTableData(invoiceData?.data);
    }
  }, [invoiceData]);

  const getMaskedBankAccount = (
    bankAccount: string | undefined
  ): string | null => {
    return bankAccount ? bankAccount.replace(/.(?=.{4})/g, "x") : "N/A";
  };
  function formatDate(date) {
    if (!date) {
      return "N/A";
    }
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    return [month, day, year].join("/");
  }
  const getDescription = (rowData): string => {
    if (rowData?.length === 0) {
      return "Account#: N/A DUE N/A; Service N/A to N/A";
    }
    const autoPayDescription = rowData?.autoPayName
      ? rowData?.autoPayName?.toLowerCase() === "eft"
        ? getMaskedBankAccount(rowData?.bankAccount)
        : `AUTOPAY ${getMaskedBankAccount(rowData?.bankAccount)}`
      : "";
    const accountNumber = rowData?.accountNumber
      ? rowData?.accountNumber
      : "N/A";
    return `Account#: ${accountNumber} ${autoPayDescription} DUE ${formatDate(
      rowData?.duedate
    )}; Service date ${formatDate(rowData?.servicePeriodDate)} to ${formatDate(
      rowData?.billingPeriodEndDate
    )} `;
  };

  const onClickBatchId = (rowData) => {
    const description = getDescription(rowData);
    const itemizes = [] as Array<IItemize>;
    if (rowData?.itemizeDetails && rowData?.itemizeDetails?.length > 0) {
      const mappedItemized = rowData?.itemizeDetails?.map((x, index) => {
        return {
          ...x,
          unitCost: x.unitCost?.toFixed(2),
          extCost: x.extCost?.toFixed(2),
          amount: x.amount?.toFixed(2),
          isActive: true,
          parentRow: index > 0 ? false : true,
          tableName: "itemizeDetails",
        };
      });
      itemizes.push(...mappedItemized);
    }
    setIsShow(true);
    setViewData({
      ...rowData,
      itemizeDetails: itemizes,
      description: description,
    });
  };

  const {
    data: completedInvoiceData,
    mutate: completedInvoiceById,
    isSuccess: completedSuccess,
    reset: resetCompletedInvoiceById,
  } = useGetCompletedInvoiceById();

  if (completedSuccess && completedInvoiceData) {
    if (
      completedInvoiceData?.status === 201 ||
      completedInvoiceData?.status === 200
    ) {
      setCompletedInvoiceDetails(completedInvoiceData.data);
    }
    resetCompletedInvoiceById();
  }

  const {
    data: voidedInvoiceData,
    mutate: getVoidedInvoiceById,
    isSuccess: getVoidedSuccess,
    reset: resetGetVoidedInvoice,
  } = useGetVoidedInvoiceById();

  if (getVoidedSuccess && voidedInvoiceData) {
    if (
      voidedInvoiceData?.status === 201 ||
      voidedInvoiceData?.status === 200
    ) {
      setVoidedInvoiceDetails(voidedInvoiceData.data);
    }
    resetGetVoidedInvoice();
  }

  const { data: activityHistoryData, mutate: getactivityHistoryById } =
    useGetActivityHistoryById();
  const matColumns = [
    {
      title: "Batch No.",
      field: "id",
      render: (rowData) => {
        return (
          <div
            className="blueAndBold underline cursor-pointer "
            onClick={() => {
              onClickBatchId(rowData);
              const id = { Id: rowData?.id };
              completedInvoiceById(id);
              getVoidedInvoiceById(id);
              getactivityHistoryById(rowData?.id);
              queryClient.invalidateQueries("GetCompletedInvoices");
            }}
          >
            {rowData.id}
          </div>
        );
      },
    },
    {
      title: "Service Account No.",
      field: "accountNumber",
    },
    {
      title: "Invoice No.",
      field: "invoiceNumber",
    },
    {
      title: "Amount Paid",
      field: "amountPaid",
    },
    {
      title: "Payment Reference No.",
      field: "paymentRef",
    },
    {
      title: "Bill Reference No.",
      field: "billRefNumber",
      render: (rowData) => {
        return (
          <div
            className="blueAndBold underline cursor-pointer "
            onClick={() => {
              window.open(
                `https://acumaticastg.confie.com/Main?CompanyID=Admin+Retail&ScreenId=AP301000&DocType=INV&RefNbr=${rowData?.billRefNumber}`,
                "_blank"
              );
            }}
          >
            {rowData?.billRefNumber}
          </div>
        );
      },
    },
    {
      title: "Date Created",
      field: "createdDate",
      render: (rowdata) => {
        return (
          <div>
            {rowdata.createdDate
              ? new Date(rowdata.createdDate).toLocaleDateString()
              : "unspecified"}
          </div>
        );
      },
    },
  ];

  const onClickCancelShow = () => {
    setIsShow(false);
  };

  return (
    <>
      <div>
        {isShow === true ? (
          <div className="relative  ">
            <CompletedDetails
              cancelShow={onClickCancelShow}
              rowData={viewData}
              activityHistoryData={activityHistoryData}
            />
          </div>
        ) : (
          <div className=" gap-2  bg-white min-h-fit">
            <div className="flex justify-between items-center py-2 ">
              <div>
                <div className="flex gap-2 items-center">
                  <div>
                    <CheckBadgeIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
                  </div>
                  <div className="flex flex-col leading tight">
                    <h3 className="text-xs font-medium text-gray-500">
                      Vendor Invoice Processing
                    </h3>
                    <h1 className="mr-auto font-bold text-primary text-md uppercase">
                      Batch List
                    </h1>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-xs  space-y-2">
              <div className="ml-auto flex justify-between pb-2 ">
                <div className="space-x-4 mr-6 ">
                  <button
                    className="hover:scale-90 transition duration-500 focus:bg-primary focus:text-white flex justify-center items-center shadow-md px-3 py-1 h-7 text-xs gap-x-2 font-semibold text-primary border hover:bg-primary selected hover:text-white border-primary rounded-md"
                    onClick={() => {
                      setFilter(!filter);
                    }}
                  >
                    <div>Filter By</div>
                    <FunnelIcon className="h-3 w-3 focus:bg-primary" />
                  </button>
                </div>
              </div>
            </div>
            <main className="flex flex-row pt-2">
              {filter && (
                <div className="overflow-y-auto  flex-col flex-grow bg-white">
                  <CompletedFilters
                    data={invoiceTableData}
                    setFilteredCompleted={setFilteredCompleted}
                    setIsSearching={setIsSearching}
                  />
                </div>
              )}
              <div
                className={
                  !filter
                    ? "false w-full h-5/6 bg-white overflow-x-auto box-shadow  "
                    : "w-4/5 h-5/6 bg-white overflow-y-auto box-shadow"
                }
              >
                <MaterialInvoiceTable
                  columns={matColumns}
                  data={isSearching ? filteredCompleted : invoiceTableData}
                  isLoading={vendorInvoiceFetching}
                />
              </div>
            </main>
          </div>
        )}
      </div>
    </>
  );
};
